import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Box, Center, Stack } from '@mantine/core';

import { Button, SectionText, SectionTitle, TextInput } from 'components';
import { CreditService } from 'services';
import { RouteConstants, ServiceConstants } from '../../constants';
import { IRootState } from 'config/redux';
import { useCallback, useEffect, useState } from 'react';
import { AppUtils } from 'utils';

const { ROUTE_CREDIT_REQUEST_DOCUMENTATION, ROUTE_CREDIT_REQUEST_FORM } = RouteConstants;
const { REQUEST_CREDIT_VERIFY_ID, REQUEST_CREDIT_REQUEST_ID } = ServiceConstants;

const CreditRequestVerifyPage = () => {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const verifyServiceObject = useSelector((state: IRootState) => state.service.object[REQUEST_CREDIT_VERIFY_ID]);
  const getServiceObject = useSelector((state: IRootState) => state.service.object[REQUEST_CREDIT_REQUEST_ID]);

  const [accessCode, setAccessCode] = useState<string>('');
  const [accessCodeError, setAccessCodeError] = useState<string>('');

  useEffect(() => {
    AppUtils.resolveServiceObject(
      verifyServiceObject,
      REQUEST_CREDIT_VERIFY_ID,
      () => {
        CreditService.getRequest(id!);
      },
      () => setAccessCodeError(t('input.verify.invalid'))
    );
  }, [verifyServiceObject]);

  useEffect(() => {
    AppUtils.resolveServiceObject(
      getServiceObject,
      REQUEST_CREDIT_REQUEST_ID,
      (val) => {
        if (val.requestedAt) {
          navigate(`${ROUTE_CREDIT_REQUEST_DOCUMENTATION}?cid=${id}`);
        } else {
          navigate(`${ROUTE_CREDIT_REQUEST_FORM}?cid=${id}`);
        }
      }
    );
  }, [getServiceObject]);

  const verifyCode = useCallback(() => {
    setAccessCodeError('');
    if (!isCodeValid(accessCode)) {
      setAccessCodeError(t('input.verify.invalid'));
      return;
    }
    CreditService.verify({ accessCode, identification: id! });
  }, [accessCode, id]);

  const isCodeValid = (code: string) => {
    return code?.trim().length === 6 && /^\d{6}$/.test(code);
  }

  return (
    <Box pt={100} bg='linear-gradient(180deg, #123F0A 0%, #4D9140 100%)' p={20} w='100%' h='100vh'>
      <Center mt={20}>
        <Stack>
          <SectionTitle variant='light' title={t('credit.verify.title')} />
          <SectionText text={t('credit.verify.description')} />
          <Box>
            <TextInput
              label={t('input.verify.label')}
              value={accessCode}
              onChange={(event) => setAccessCode(event.currentTarget.value)}
              labelLight={true}
              error={accessCodeError}
            />
            <Button mt={24} type={'submit'} disabled={!isCodeValid(accessCode)} onClick={verifyCode}>
              {t('action.verify')}
            </Button>
          </Box>
        </Stack>
      </Center>
    </Box>
  )
}

export default CreditRequestVerifyPage;